import { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import useRouter from 'use-react-router';
import { useActions } from 'utils/hooks';

import Table, { useTable } from 'common/components/table';
import BasicModal from 'common/components/modals/BasicModal';
import useDrawer from 'common/components/drawer/useDrawer';
import { strToNumber } from 'common/utils/numbers';

import * as portActions from 'store/ports/actions';
import * as tableListActions from 'store/tables/lists/actions';

import TableActions from 'components/layouts/table';
import Map from 'components/map';
import PageLoader from 'common/components/general/PageLoader';

import {
  portsColumns,
  portsMapColumns,
  portMapRows,
  portRows,
  portMapTopFilters
} from './tableConfig';
import Form from './form';
import EnumsDropdown from '../Dropdown';
import TopFilters from './TopFilters';
import EmptyState from './EmptyState';
import useShouldShowEmptyState from './useShouldShowEmptyState';
import { portNoAreaOption, portNoCountryOption } from 'views/enums/ports/form/port/constants';
import { selectListOptionsFromStore } from 'store/lists/selectors';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { fetchListOptions } from 'store/lists/actions';

const Ports = () => {
  const [active, setActive] = useState(null);
  const [map, toggleMap] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const sizeOptions = useSelector(state => selectListOptionsFromStore(state, 'port-sizes'));

  const { match } = useRouter();
  const { path } = match;
  const isMapView = path?.includes('map');

  const [archivePort, unArchivePort, updatePort, clearTableMapPorts, getTableList] = useActions([
    portActions.archivePort,
    portActions.unArchivePort,
    portActions.updatePort,
    portActions.clearTableMapPorts,
    tableListActions.getTableList
  ]);

  const fetchData = async params => {
    if (!params) return;

    const { filters, ...rest } = params;

    const hasFilters = filters?.some(f => f.name === 'port_area_id' || f.name === 'country_id');

    if (!hasFilters && isMapView) return;

    const modifiedFilters = filters.map(f => {
      return {
        ...f,
        value: [portNoAreaOption.id, portNoCountryOption.id].includes(f.value) ? [null] : f.value
      };
    });

    try {
      const res = await getTableList({
        ...rest,
        filters: modifiedFilters
      });

      return res;
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    dispatch(fetchListOptions('port-sizes'));
  }, [dispatch]);

  const drawer = useDrawer();
  const table = useTable({
    label: isMapView ? 'ports_map' : 'ports',
    requestTableListFrom: fetchData,
    urlRefetchLevel: 3,
    columns: isMapView ? portsMapColumns : portsColumns,
    top: isMapView ? portMapTopFilters : null
  });

  const constructKey = key => {
    switch (key) {
      case 'area.name':
        return 'area';
      default:
        return key;
    }
  };

  const onCellSave = async ({ data, value, key }) => {
    const { id } = data;

    if (!id) return;

    const constructedKey = constructKey(key);

    try {
      setIsLoading(true);
      const params = {
        id,
        [constructedKey]: value,
        table: 'ports_map'
      };

      await updatePort(params);
      setIsLoading(false);
      table.refetchData();
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const selectRows = isMapView
    ? portMapRows({ onCellSave, setActive, toggleMap, drawer })
    : portRows({ onCellSave, setActive, toggleMap, drawer, sizeOptions });

  const showEmptyState = useShouldShowEmptyState({ table });

  const { data } = table.state;

  useEffect(() => {
    if (showEmptyState) {
      clearTableMapPorts({ table: 'ports_map' });
    }
  }, [showEmptyState]);

  const mapToPorts = datum => ({
    actions: [],
    port_name: datum.name,
    port_code: datum.code,
    port_id: datum.id,
    position: { lng: strToNumber(datum.lon), lat: strToNumber(datum.lat) }
  });

  const ports = useMemo(() => {
    return showEmptyState ? [] : data?.map(mapToPorts);
  }, [showEmptyState, data]);

  return (
    <Row className="ports-table">
      <Col xs={12} className="mb-6 mt-n1">
        <TableActions
          documentTitle="Ports"
          title="Enums"
          dropdown={<EnumsDropdown />}
          actions={[
            { type: 'search' },
            {
              type: 'add',
              text: 'Add Port',
              color: 'primary',
              onClick: () => {
                setActive(null);
                drawer.open();
              }
            }
          ]}
        />
      </Col>

      <Col xs={12} className={`${isLoading ? 'pointer-events-none' : ''}`}>
        <PageLoader isLoading={isLoading} />
        <Table
          loader
          showArchived={!isMapView}
          hideTableFilters={isMapView}
          hideTopPagination={isMapView}
          hideTableSearch={isMapView}
          onArchive={archivePort}
          onUnarchive={unArchivePort}
          topCustomComponent={
            isMapView ? (
              <div className="w-100p h-100p ports-map mt-1 mb-1">
                <Map showTooltip canMaximize defaultView="simple" maxZoom={10} ports={ports} />
              </div>
            ) : null
          }
          topFiltersComponent={<TopFilters />}
          drawer={<Form drawer={drawer} active={active} />}
          showEmptyState={showEmptyState}
          emptyStateComponent={<EmptyState />}
          rows={selectRows}
          {...table}
        />

        {isMapView ? null : (
          <BasicModal
            isOpen={map}
            toggle={() => toggleMap(!map)}
            header={active?.name || '-'}
            className="ports-map-modal"
            body={
              active && active?.lat && active?.lon ? (
                <Map
                  maxZoom={10}
                  ports={[
                    {
                      actions: [],
                      position: { lng: strToNumber(active?.lon), lat: strToNumber(active?.lat) }
                    }
                  ]}
                />
              ) : null
            }
          />
        )}
      </Col>
    </Row>
  );
};

export default Ports;
